import {Component, OnInit, inject} from '@angular/core';

import {TranslateModule} from '@ngx-translate/core';
import {IxActiveModal, IxModule} from '@siemens/ix-angular';
import {ListBatteryObject} from '../../../../models/ListBattery';
import {PassportService} from '../../../../services/passport.service';
import {HierarchyGraphComponent} from '../../../shared/hierarchy-graph/hierarchy-graph.component';
import {PassportSearchSelectComponent} from '../../../shared/passport-search-select/passport-search-select.component';

@Component({
  selector: 'app-modal-edit-hierarchy',
  imports: [
    IxModule,
    TranslateModule,
    HierarchyGraphComponent,
    PassportSearchSelectComponent,
  ],
  templateUrl: './modal-edit-hierarchy.component.html',
  styleUrl: './modal-edit-hierarchy.component.scss',
})
export class ModalEditHierarchyComponent implements OnInit {
  readonly activeModal = inject(IxActiveModal);
  readonly passportService = inject(PassportService);

  loading = true;
  waitForSaving = false;
  passport!: ListBatteryObject;
  ancestor?: ListBatteryObject;
  ancestorErrorMessage?: string;
  descendants!: ListBatteryObject[];
  descendantErrorMessage?: string;
  excludedPassports: string[] = [];

  ngOnInit(): void {
    this.loading = true;
    this.loadData(this.activeModal.data.id).then(() => {
      this.loading = false;
    });
  }

  async loadData(passportId: string) {
    this.ancestor =
      (await this.passportService.loadPassportAncestor(passportId)).data ||
      undefined;
    this.descendants = (
      await this.passportService.loadPassportDescendants(passportId)
    ).data;
    this.passport = (await this.passportService.loadList({passportId})).data[0];
    this.mapExcludedPassports();
  }

  mapExcludedPassports() {
    this.excludedPassports = [
      this.passport?.id,
      this.ancestor?.id,
      ...(this.descendants?.map((d) => d.id) ?? []),
    ].filter(Boolean) as string[];
  }

  onAddAncestor(ancestorPassport: ListBatteryObject) {
    if (ancestorPassport.id) {
      this.ancestor = ancestorPassport;
      this.mapExcludedPassports();
    }
  }

  onAddDescendant(descendantPassport: ListBatteryObject) {
    if (descendantPassport.id) {
      const descendantsCopy = this.descendants.map((d) => d);
      descendantsCopy.push(descendantPassport);
      this.descendants = descendantsCopy;
      this.mapExcludedPassports();
    }
  }

  async onRemoveAncestor() {
    this.ancestor = undefined;
    this.ancestorErrorMessage = undefined;
    this.mapExcludedPassports();
  }

  onRemoveDescendant(descendantPassport: ListBatteryObject) {
    this.descendants = this.descendants.filter(
      (d) => d.id !== descendantPassport.id,
    );
    this.descendantErrorMessage = undefined;
    this.mapExcludedPassports();
  }

  async onSaveHierarchy() {
    this.waitForSaving = true;
    let errorWhileSaving = false;
    if (!this.ancestor) {
      await this.passportService.deletePassportAncestor(this.passport.id);
    } else {
      const latestAncestor = (
        await this.passportService.loadPassportAncestor(this.passport.id)
      ).data;
      if (this.ancestor.id !== latestAncestor?.id) {
        try {
          await this.passportService.addPassportAncestor(
            this.passport.id,
            this.ancestor.id,
            true,
          );
        } catch (error) {
          this.ancestorErrorMessage = (error as {message: string}).message;
          errorWhileSaving = true;
        }
      }
    }

    const latestDescendants = (
      await this.passportService.loadPassportDescendants(this.passport.id)
    ).data;
    try {
      await this.syncDescendants(latestDescendants, this.descendants);
      this.descendantErrorMessage = undefined;
    } catch (error) {
      this.descendantErrorMessage = (error as {message: string}).message;
      errorWhileSaving = true;
    }

    if (!errorWhileSaving) {
      this.activeModal.close(true);
    }
    this.waitForSaving = false;
    this.loading = false;
  }

  /**
   * Synchronizes the descendants between backend and current selection.
   * @param backendDescendants the descendants from the backend
   * @param currentDescendants the currently selected descendants
   */
  async syncDescendants(
    backendDescendants: ListBatteryObject[],
    currentDescendants: ListBatteryObject[],
  ): Promise<void> {
    const backendIds = new Set(backendDescendants.map((d) => d.id));
    const currentIds = new Set(currentDescendants.map((d) => d.id));

    const toRemove = backendDescendants.filter((d) => !currentIds.has(d.id));
    const toAdd = currentDescendants.filter((d) => !backendIds.has(d.id));

    for (const descendant of toRemove) {
      await this.passportService.deletePassportAncestor(descendant.id);
    }
    for (const descendant of toAdd) {
      try {
        await this.passportService.addPassportAncestor(
          descendant.id,
          this.passport.id,
          true,
        );
      } catch (e) {
        throw new Error((e as any).message);
      }
    }
  }
}
